/**
 * CDB-TQS API — Agent Toolbox Skills (cdb_tqs#307)
 */

import { CDB_TQS_API_BASE_URL, fetchWithLogging } from "./http";

export const toolboxApi = {
  async listSkills(opts?: {
    activeOnly?: boolean
    visibility?: import('@/types/toolbox').Visibility
  }): Promise<import('@/types/toolbox').SkillListResponse> {
    const params: Record<string, string | boolean> = {}
    if (opts?.activeOnly !== undefined) params.active_only = opts.activeOnly
    if (opts?.visibility) params.visibility = opts.visibility
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/skills`,
      { method: 'GET', params },
    )
    return response.data
  },

  async getSkill(id: string): Promise<import('@/types/toolbox').Skill> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/skills/${id}`,
      { method: 'GET' },
    )
    return response.data
  },

  async createSkill(
    data: import('@/types/toolbox').SkillCreate,
  ): Promise<import('@/types/toolbox').Skill> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/skills`,
      { method: 'POST', body: JSON.stringify(data) },
    )
    return response.data
  },

  async updateSkill(
    id: string,
    data: import('@/types/toolbox').SkillUpdate,
  ): Promise<import('@/types/toolbox').Skill> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/skills/${id}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    )
    return response.data
  },

  async deleteSkill(id: string): Promise<void> {
    await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/skills/${id}`, {
      method: 'DELETE',
    })
  },

  /**
   * UPSERT a skill's permission row (migration 077). The backend uses
   * ``PUT /skills/{id}/permission`` because there's exactly one
   * permission row per skill — replace, don't append.
   */
  async upsertSkillPermission(
    skillId: string,
    data: import('@/types/toolbox').SkillPermissionCreate,
  ): Promise<import('@/types/toolbox').SkillPermission> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/skills/${skillId}/permission`,
      { method: 'PUT', body: JSON.stringify(data) },
    )
    return response.data
  },

  async listDiscoverableEndpoints(
    includeIneligible: boolean = false,
  ): Promise<import('@/types/toolbox').DiscoverableEndpointsResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/skills/discoverable-endpoints`,
      { method: 'GET', params: { include_ineligible: includeIneligible } },
    )
    return response.data
  },

  /**
   * Catalog of server-side capability-handler types (cdb_tqs#512). Drives the
   * handler-skill create form (parameter_schema = agent-visible params,
   * config_schema = handler_config form) — analogous to the router
   * connector-type catalog, no frontend deploy per handler.
   */
  async listHandlerTypes(
    enabledOnly: boolean = false,
  ): Promise<import('@/types/toolbox').SkillHandlerTypesResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/skills/handler-types`,
      { method: 'GET', params: { enabled_only: enabledOnly } },
    )
    return response.data
  },

  /**
   * Turn telemetry aggregates for Communicator 2.0 (#486, cdb_tqs#481 Phase 1).
   * Server-side p50/p95 over tqs.chat_turn_metrics — metadata only, no
   * query/answer text exists at the source.
   */
  async getTurnMetricsSummary(
    days: number = 7,
  ): Promise<import('@/types/toolbox').TurnMetricsSummary> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/v2/metrics/summary`,
      { method: 'GET', params: { days: String(days) } },
    )
    return response.data
  },

  /**
   * Recent turns with their routing provenance for the Per-Turn-Trace tab
   * (cdb_tqs#481 Phase D-3): Layer-1 directions + curation meta + latency,
   * newest first. Metadata only — no query/answer text at the source.
   */
  async getRecentTurns(
    limit: number = 50,
  ): Promise<import('@/types/toolbox').RecentTurnsResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/v2/metrics/recent-turns`,
      { method: 'GET', params: { limit: String(limit) } },
    )
    return response.data
  },

  /**
   * Recent conversations for the Session-Trace picker (cdb_tqs#604): one entry
   * per conversation_id with turn/error/empty counts and the activity window,
   * most-recent first. Metadata only.
   */
  async getRecentSessions(
    limit: number = 50,
  ): Promise<import('@/types/toolbox').RecentSessionsResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/v2/metrics/sessions`,
      { method: 'GET', params: { limit: String(limit) } },
    )
    return response.data
  },

  /**
   * Session-wide turn trace for one conversation (cdb_tqs#604): every turn
   * oldest-first with its routing provenance, so a routing test can be walked
   * top-to-bottom to find where it misroutes. Metadata only.
   */
  async getSessionTrace(
    conversationId: string,
    limit: number = 500,
  ): Promise<import('@/types/toolbox').SessionTraceResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/v2/metrics/session-trace`,
      {
        method: 'GET',
        params: { conversation_id: conversationId, limit: String(limit) },
      },
    )
    return response.data
  },

  /**
   * Satisfaction aggregated by Layer-1 routing direction (cdb_tqs#605): per
   * direction the rating count, negative share and most common 👎 reason.
   * Metadata only.
   */
  async getFeedbackByDirection(
    days: number = 30,
  ): Promise<import('@/types/toolbox').FeedbackByDirectionResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/v2/metrics/feedback-by-direction`,
      { method: 'GET', params: { days: String(days) } },
    )
    return response.data
  },

  /** Effective manifest-curator config (cdb_tqs#488). */
  async getRoutingConfig(): Promise<import('@/types/toolbox').RoutingConfig> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/v2/routing-config`,
      { method: 'GET' },
    )
    return response.data
  },

  /** Update curator config (admin-only server-side; whitelisted keys). */
  async updateRoutingConfig(
    values: Partial<import('@/types/toolbox').RoutingConfig>,
  ): Promise<import('@/types/toolbox').RoutingConfig> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/v2/routing-config`,
      { method: 'PUT', body: JSON.stringify(values) },
    )
    return response.data
  },

  async invokeSkill(
    id: string,
    args: Record<string, unknown> = {},
  ): Promise<unknown> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/skills/${id}/invoke`,
      { method: 'POST', body: JSON.stringify({ args }) },
    )
    return response.data
  },

  async testSkill(
    id: string,
    args: Record<string, unknown> = {},
  ): Promise<unknown> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/skills/${id}/test`,
      { method: 'POST', body: JSON.stringify({ args }) },
    )
    return response.data
  },
};
